// Catalog presentation model — maps product jargon to the customer's language
// and ranks/filters the catalog for the self-service picker.
//
// NOTE(rules): these are name-based heuristics living in the client for now.
// Per project convention ("server-side rules"), the tecla↔canal mapping and
// suggestion ranking should eventually move to SYW Postgres rulesets; keep
// this module as the single place to delete when that happens.

import type { SywComponent } from '../hooks/useSywData';

// Accent/case-insensitive normalization for matching PT names.
function norm(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

// "Módulo 2 Canais WiFi" → 2 · "Interruptor 1 tecla" → 1 · otherwise null.
export function teclasOf(c: Pick<SywComponent, 'name'>): number | null {
  const n = norm(c.name);
  const canal = /(\d+)\s*can(?:al|ais)\b/.exec(n);
  if (canal?.[1]) return Number.parseInt(canal[1], 10);
  const tecla = /(\d+)\s*teclas?\b/.exec(n);
  if (tecla?.[1]) return Number.parseInt(tecla[1], 10);
  return null;
}

// Customer-facing label: rewrites "N Canal/Canais" as "N tecla(s)".
// "Módulo 1 Canal WiFi vPRO" → "Módulo 1 tecla WiFi vPRO".
export function friendlyName(c: Pick<SywComponent, 'name'>): string {
  return c.name.replace(/(\d+)\s*can(?:al|ais)\b/gi, (_m, d: string) =>
    Number.parseInt(d, 10) === 1 ? `${d} tecla` : `${d} teclas`,
  );
}

export function isVpro(c: Pick<SywComponent, 'name'>): boolean {
  return norm(c.name).includes('vpro');
}

export function isInterruptor(c: Pick<SywComponent, 'name'>): boolean {
  return teclasOf(c) !== null || norm(c.name).includes('interruptor');
}

export function isTomada(c: Pick<SywComponent, 'name'>): boolean {
  return norm(c.name).includes('tomada');
}

// Products whose colour is a cosmetic variant (frames + switch modules).
export function isColorable(c: Pick<SywComponent, 'name'>): boolean {
  const n = norm(c.name);
  return n.includes('espelho') || n.includes('modulo') || isInterruptor(c) || isTomada(c);
}

export function isEspelho(c: Pick<SywComponent, 'name'>): boolean {
  return norm(c.name).includes('espelho');
}

// Nº of aparelhagem boxes an espelho covers: "Espelho Simples 1 Módulo" → 1,
// "Espelho Duplo" → 2, etc. Null when the name doesn't say.
export function boxesOf(c: Pick<SywComponent, 'name'>): number | null {
  const n = norm(c.name);
  if (!n.includes('espelho')) return null;
  const num = /(\d+)\s*modulos?\b/.exec(n);
  if (num?.[1]) return Number.parseInt(num[1], 10);
  if (n.includes('simples')) return 1;
  if (n.includes('duplo')) return 2;
  if (n.includes('triplo')) return 3;
  if (n.includes('quadruplo')) return 4;
  return null;
}

// Distinct colour names present in a set of catalog rows (Shopify variants).
export function colorsOf(components: Array<Pick<SywComponent, 'colorName'>>): string[] {
  return [...new Set(components.map((c) => c.colorName).filter((c): c is string => !!c))].sort(
    (a, b) => a.localeCompare(b),
  );
}

// Ranking for the "Top sugestões" rail: vPRO interruptores → vPRO tomadas →
// other interruptores → other tomadas (per Rafiq's priority order).
function suggestionRank(c: SywComponent): number {
  const vpro = isVpro(c);
  if (vpro && isInterruptor(c)) return 0;
  if (vpro && isTomada(c)) return 1;
  if (isInterruptor(c)) return 2;
  if (isTomada(c)) return 3;
  return 4;
}

export function topSuggestions(components: SywComponent[], limit = 6): SywComponent[] {
  return components
    .filter((c) => c.active && suggestionRank(c) < 4)
    .sort((a, b) => suggestionRank(a) - suggestionRank(b))
    .slice(0, limit);
}

// Token search across name / description / SKU, accent-insensitive.
export function searchCatalog(components: SywComponent[], query: string): SywComponent[] {
  const tokens = norm(query).split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return components;
  return components.filter((c) => {
    const hay = norm(`${c.name} ${c.description ?? ''} ${c.sku} ${friendlyName(c)}`);
    return tokens.every((t) => hay.includes(t));
  });
}

// ─── Custom-order espelho helpers ────────────────────────────────────────────

// An espelho is custom-order when: the remote pricing flag says so (authoritative),
// OR (fallback for catalog-only callers) it is an espelho with null price.
export function isCustomOrderEspelho(
  comp: Pick<SywComponent, 'name'>,
  pricingEur: number | null,
  isCustomOrderFlag?: boolean,
): boolean {
  if (isCustomOrderFlag !== undefined) return isCustomOrderFlag;
  return isEspelho(comp) && pricingEur === null;
}

// Closest priced non-custom espelho to suggest as an immediate alternative.
// Ranks: same color → fewest-extra-boxes (+1 box preferred over -1 over larger
// gaps). isCustomOrderBySku excludes espelhos that are also custom-order.
export function closestEspelhoSuggestion(
  targetBoxes: number | null,
  targetColor: string | null,
  catalog: SywComponent[],
  isCustomOrderBySku?: (sku: string) => boolean,
): SywComponent | null {
  const candidates = catalog.filter(
    (c) =>
      c.active &&
      isEspelho(c) &&
      c.priceEur > 0 &&
      !(isCustomOrderBySku?.(c.sku) ?? false),
  );
  if (candidates.length === 0) return null;

  function score(c: SywComponent): number {
    let s = 0;
    if (targetColor && c.colorName !== targetColor) s += 3;
    const cb = boxesOf(c);
    if (targetBoxes !== null && cb !== null) {
      const diff = cb - targetBoxes;
      s += diff === 0 ? 0 : diff === 1 ? 0.5 : diff === -1 ? 2 : Math.abs(diff) * 4;
    } else {
      s += 10;
    }
    return s;
  }

  return [...candidates].sort((a, b) => score(a) - score(b))[0] ?? null;
}

// Smartify-branded note for an espelho suggestion that has extra slots/keys.
export function extraKeyNote(
  targetBoxes: number | null,
  suggBoxes: number | null,
): string | null {
  if (targetBoxes === null || suggBoxes === null) return null;
  const diff = suggBoxes - targetBoxes;
  if (diff === 1) {
    return 'A tecla extra pode ser usada para cenários de iluminação ou para apagar as luzes de outra divisão.';
  }
  if (diff > 1) {
    return `As ${diff} teclas extra podem ser usadas para cenários, controlo de outras divisões ou automações personalizadas.`;
  }
  return null;
}

// ─── Swatch colours ──────────────────────────────────────────────────────────

// Swatch colours for common PT/EN variant names; neutral gray when unknown.
const SWATCH: Record<string, string> = {
  branco: '#f8f8f8', white: '#f8f8f8',
  preto: '#1a1a1a', black: '#1a1a1a',
  cinza: '#8a8f98', cinzento: '#8a8f98', gray: '#8a8f98', grey: '#8a8f98',
  aluminio: '#c0c4c8', alumínio: '#c0c4c8', silver: '#c0c4c8', prata: '#c0c4c8',
  antracite: '#383e42', anthracite: '#383e42',
  dourado: '#d4af37', gold: '#d4af37',
  champagne: '#ead9b6', creme: '#f1e8d7', bege: '#d9c8a9',
  castanho: '#6b4a2f', brown: '#6b4a2f',
};

export function swatchHex(colorName: string): string {
  return SWATCH[norm(colorName)] ?? '#9ca3af';
}
